import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  IconButton
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { useNavigate } from 'react-router-dom';
import { stripeStatusTrial } from 'src/common/config/TextMessage';
import ChangePlanNew from 'src/content/billing/components/ChangePlanNew';
import CommonModal from 'src/common/config/CommonModal';
import CancelSubscription from 'src/content/billing/components/CancelSubscription';
import { API } from 'src/common/config/API';

export const PlanTrialDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccessMsg, setCancelSuccessMsg] = useState<string | undefined>(
    undefined
  );

  const activePlanName =
    useSelector((state: RootState) => state.user.activePlan) || '';
  const stripeSubscriptionDetails = useSelector(
    (state: RootState) => state.user.subscription
  );
  const userDetails: any =
    useSelector((state: RootState) => state.counter.userDetails) ?? {};
  const scanDetailRedux: any = useSelector(
    (state: RootState) => state.counter.scanDetails
  );
  const userEmail = useSelector((state: RootState) => state.counter.userEmail);

  const isOldPlan = stripeSubscriptionDetails?.planVersion === 'v1';

  // Format plan display name from actual subscription plan
  const getPlanName = () => {
    const rawName =
      stripeSubscriptionDetails?.plan_name?.name ||
      stripeSubscriptionDetails?.plan?.name ||
      activePlanName ||
      '';
    const norm = rawName.toLowerCase();
    if (
      norm === 'designer' ||
      norm === 'proplus' ||
      norm === 'pro plus' ||
      norm === 'pro'
    )
      return 'Pro';
    if (
      norm === 'agency' ||
      norm === 'expertplus' ||
      norm === 'expert plus' ||
      norm === 'expert'
    )
      return 'Expert';
    if (
      norm === 'diy' ||
      norm === 'starterplus' ||
      norm === 'starter plus' ||
      norm === 'starter'
    )
      return isOldPlan ? 'DIY' : 'Starter';
    return rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : 'Pro';
  };

  const planDisplayName = getPlanName();

  // Determine if user is in trial strictly dynamically
  const isTrial =
    stripeSubscriptionDetails?.status === stripeStatusTrial ||
    stripeSubscriptionDetails?.status === 'trialing';

  // Calculate days left dynamically
  const periodEnd =
    stripeSubscriptionDetails?.trial_end ||
    stripeSubscriptionDetails?.current_period_end;
  const nowSec = Math.floor(Date.now() / 1000);

  let daysLeft: number | null = null;
  if (isTrial && periodEnd && periodEnd > nowSec) {
    daysLeft = Math.max(0, Math.ceil((periodEnd - nowSec) / (24 * 3600)));
  }

  // Format renewal date dynamically
  const formatDateShort = (timestampInSec?: number) => {
    if (!timestampInSec) return '';
    const date = new Date(timestampInSec * 1000);
    if (isNaN(date.getTime())) return '';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const renewDateStr = formatDateShort(periodEnd);

  // Total site capacity dynamically
  const totalSites =
    scanDetailRedux?.['scan'] ||
    userDetails?.subscription?.scan ||
    stripeSubscriptionDetails?.scan ||
    null;

  const sitesTitleText =
    planDisplayName && totalSites !== null
      ? `${planDisplayName} · ${totalSites} ${
          totalSites === 1 ? 'site' : 'sites'
        }`
      : planDisplayName ||
        (totalSites !== null
          ? `${totalSites} ${totalSites === 1 ? 'site' : 'sites'}`
          : '');

  const [allPlans, setAllPlans] = useState<any[]>([]);

  useEffect(() => {
    try {
      API.allPlan(
        API.EndPoint.allPlan,
        API.Method.get,
        undefined,
        {},
        (response: any) => {
          if (
            response?.data?.status === 'success' &&
            Array.isArray(response?.data?.plans)
          ) {
            setAllPlans(response.data.plans);
          }
        }
      );
    } catch (err) {}
  }, []);

  // Amount & Interval formatting (Dynamic price from active user subscription / plan)
  const interval =
    stripeSubscriptionDetails?.interval ||
    stripeSubscriptionDetails?.plan?.recurring?.interval ||
    stripeSubscriptionDetails?.recurring?.interval;

  const isYearly = interval === 'year' || interval === 'annually';

  // Match user's actual plan from allPlans API if direct amount not in subscription
  const matchedPlan =
    allPlans.find((p: any) => {
      const pId = p?.id;
      const subPlanId =
        stripeSubscriptionDetails?.plan_name?.id ||
        stripeSubscriptionDetails?.plan?.id ||
        stripeSubscriptionDetails?.plan_id;
      return (
        pId &&
        subPlanId &&
        pId === subPlanId &&
        p?.recurring?.interval === (isYearly ? 'year' : 'month')
      );
    }) ||
    allPlans.find((p: any) => {
      const pMetaName = (
        p?.metadata?.name ||
        p?.metadata?.display_name ||
        p?.name ||
        ''
      ).toLowerCase();
      const activeName = (activePlanName || '').toLowerCase();
      return (
        pMetaName &&
        activeName &&
        (pMetaName === activeName || pMetaName.includes(activeName)) &&
        p?.recurring?.interval === (isYearly ? 'year' : 'month')
      );
    });

  const rawAmount =
    stripeSubscriptionDetails?.unit_amount !== undefined &&
    stripeSubscriptionDetails?.unit_amount !== null
      ? stripeSubscriptionDetails.unit_amount / 100
      : stripeSubscriptionDetails?.plan_amount !== undefined &&
        stripeSubscriptionDetails?.plan_amount !== null
      ? stripeSubscriptionDetails.plan_amount
      : stripeSubscriptionDetails?.amount !== undefined &&
        stripeSubscriptionDetails?.amount !== null
      ? stripeSubscriptionDetails.amount
      : matchedPlan?.unit_amount !== undefined &&
        matchedPlan?.unit_amount !== null
      ? matchedPlan.unit_amount / 100
      : matchedPlan?.amount;

  let priceFormatted = '';
  if (
    rawAmount !== undefined &&
    rawAmount !== null &&
    !isNaN(Number(rawAmount)) &&
    Number(rawAmount) > 0
  ) {
    const formattedNum =
      typeof rawAmount === 'number'
        ? rawAmount.toFixed(2)
        : Number(rawAmount).toFixed(2);
    const intervalLabel = isYearly ? 'yr' : 'mo';
    priceFormatted = `$${formattedNum}/${intervalLabel}`;
  }

  // Construct dynamic subtext
  let subtextContent = '';
  if (isTrial) {
    if (priceFormatted && renewDateStr) {
      subtextContent = `Renews at ${priceFormatted} on ${renewDateStr}`;
    } else if (priceFormatted) {
      subtextContent = `Renews at ${priceFormatted}`;
    } else if (renewDateStr) {
      subtextContent = `Trial ends on ${renewDateStr}`;
    }
  } else {
    if (priceFormatted && renewDateStr) {
      subtextContent = `Renews at ${priceFormatted} on ${renewDateStr}`;
    } else if (renewDateStr) {
      subtextContent = `Renews on ${renewDateStr}`;
    } else if (priceFormatted) {
      subtextContent = `Renews at ${priceFormatted}`;
    }
  }

  // Progress Bar segments calculation
  const totalSegments = 7;
  const filledSegments =
    isTrial && daysLeft !== null
      ? Math.min(totalSegments, Math.max(1, daysLeft))
      : totalSegments;

  const headerLabelTextDesktop = isTrial
    ? daysLeft !== null
      ? `${planDisplayName || 'Pro'} trial · ${daysLeft} day${
          daysLeft === 1 ? '' : 's'
        } left`
      : planDisplayName
      ? `${planDisplayName} trial`
      : ''
    : planDisplayName;

  const headerLabelTextMobile = isTrial
    ? daysLeft !== null
      ? `${planDisplayName || 'Pro'} · ${daysLeft}d left`
      : planDisplayName
      ? `${planDisplayName}`
      : ''
    : planDisplayName;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleOpenChangePlan = () => {
    handleClosePopover();
    setChangePlanModalOpen(true);
  };

  const handleCloseChangePlan = () => {
    setChangePlanModalOpen(false);
  };

  // const handleOpenCancelModal = () => {
  //   handleClosePopover();
  //   setCancelModalOpen(true);
  // };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setCancelSuccessMsg(undefined);
  };

  const handleCancelSubscriptionSubmit = () => {
    setCancelLoading(true);
    try {
      API.cancelSubscription(
        API.EndPoint.cancelSubscription,
        API.Method.post,
        undefined,
        {
          email: userEmail,
          subscription_id: stripeSubscriptionDetails?.['subscription_id']
        },
        (response: any) => {
          if (response?.data?.status === 'success') {
            setCancelSuccessMsg(
              response?.data?.message || 'Subscription cancelled successfully.'
            );
            setTimeout(() => {
              setCancelLoading(false);
              handleCloseCancelModal();
            }, 2000);
          } else {
            setCancelLoading(false);
          }
        }
      );
    } catch (err) {
      setCancelLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'plan-trial-popover' : undefined;

  if (!isTrial) return null;

  return (
    <>
      {/* Dropdown Button in Header */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '4px', sm: '8px' },
          backgroundColor: 'var(--color-primary)',
          color: '#FFFFFF',
          borderRadius: '8px',
          padding: { xs: '5px 10px', sm: '6px 14px' },
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease-in-out',
          marginRight: { xs: '8px', sm: '16px' },
          userSelect: 'none',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: 'var(--color-primary)',
            opacity: 0.9,
            borderColor: '#FFFFFF'
          }
        }}
      >
        <AccessTimeRoundedIcon
          sx={{ fontSize: { xs: '16px', sm: '18px' }, color: '#FFFFFF' }}
        />
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontSize: { xs: '12px', sm: '14px' },
            fontWeight: 500,
            color: '#FFFFFF',
            lineHeight: '20px',
            whiteSpace: 'nowrap'
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {headerLabelTextDesktop}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            {headerLabelTextMobile}
          </Box>
        </Typography>
        {open ? (
          <KeyboardArrowUpRoundedIcon
            sx={{ fontSize: { xs: '16px', sm: '18px' }, color: '#FFFFFF' }}
          />
        ) : (
          <KeyboardArrowDownRoundedIcon
            sx={{ fontSize: { xs: '16px', sm: '18px' }, color: '#FFFFFF' }}
          />
        )}
      </Box>

      {/* Popover Dropdown Card */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '12px',
            boxShadow: '0px 10px 30px rgba(18, 2, 66, 0.15)',
            border: '1px solid rgba(18, 2, 66, 0.15)',
            width: { xs: 'calc(100vw - 32px)', sm: '320px' },
            maxWidth: '320px',
            marginTop: '8px'
          }
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(18, 2, 66, 0.12)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Segmented Progress Bar */}
          <Box sx={{ display: 'flex', gap: '5px', width: '100%', mb: 2 }}>
            {Array.from({ length: totalSegments }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '4px',
                  backgroundColor:
                    index < filledSegments
                      ? 'var(--color-primary)'
                      : 'rgba(18, 2, 66, 0.12)',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </Box>

          {/* Plan & Sites Capacity */}
          {sitesTitleText && (
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--color-primary)',
                lineHeight: '22px',
                mb: '4px'
              }}
            >
              {sitesTitleText}
            </Typography>
          )}

          {/* Renewal / Expiring Subtext */}
          {subtextContent && (
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '13.5px',
                fontWeight: 500,
                color: 'var(--color-primary)',
                opacity: 0.75,
                lineHeight: '20px',
                mb: 2.5
              }}
            >
              {subtextContent}
            </Typography>
          )}

          {/* Change Plan Button */}
          <Button
            variant="contained"
            onClick={handleOpenChangePlan}
            sx={{
              width: '100%',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: 'var(--color-primary)',
              color: '#FFFFFF',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              // mb: 1.5,
              '&:hover': {
                backgroundColor: 'var(--color-primary)',
                opacity: 0.9
              }
            }}
          >
            Change plan
          </Button>

          {/* Cancel Trial / Subscription Link Button */}
          {/* <Button
            variant="text"
            onClick={handleOpenCancelModal}
            disableRipple
            sx={{
              width: '100%',
              color: '#EF4444',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              padding: 0,
              minHeight: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#DC2626'
              }
            }}
          >
            {isTrial ? 'Cancel trial' : 'Cancel subscription'}
          </Button> */}
        </Box>
      </Popover>

      {/* Change Plan Modal */}
      <Dialog
        open={changePlanModalOpen}
        onClose={handleCloseChangePlan}
        sx={{
          borderRadius: '50px',
          margin: 0,
          backgroundColor: 'var(--color-bg-purple-light)'
        }}
        PaperProps={{
          style: {
            backgroundColor: 'var(--color-bg-purple-light)',
            boxShadow: 'none'
          }
        }}
        maxWidth={false}
        fullScreen={true}
        fullWidth={true}
      >
        <DialogTitle
          sx={{ padding: 0, backgroundColor: 'var(--color-bg-purple-light)' }}
        >
          <div className="flex justify-between items-center px-6 py-2">
            <Box
              sx={{
                fontFamily: 'Inter',
                fontWeight: '800 !important',
                fontSize: '18px',
                lineHeight: '29px'
              }}
            >
              <img
                src={
                  process.env.PUBLIC_URL + '/static/images/logo/new-logo.svg'
                }
                alt="Logo"
                style={{ height: '29.52px' }}
              />
            </Box>
            <IconButton onClick={handleCloseChangePlan}>
              <CloseIcon style={{ color: 'black' }} />
            </IconButton>
          </div>
        </DialogTitle>
        <div
          style={{
            overflowX: 'hidden',
            backgroundColor: 'var(--color-bg-purple-light)',
            flex: 1
          }}
          className="lg:overflow-y-auto"
        >
          <ChangePlanNew handleChangePlanModal={handleCloseChangePlan} />
        </div>
      </Dialog>

      {/* Cancel Subscription / Trial Modal */}
      <CommonModal
        open={cancelModalOpen}
        handCloseModal={handleCloseCancelModal}
        title={isTrial ? 'Cancel Trial' : 'Cancel Subscription'}
        closebtn={false}
      >
        <CancelSubscription
          handCloseModal={handleCloseCancelModal}
          handleCancelSubscription={handleCancelSubscriptionSubmit}
          loading={cancelLoading}
          subscriptionCancelMessage={cancelSuccessMsg}
        />
      </CommonModal>
    </>
  );
};

export default PlanTrialDropdown;
